import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import dayjs, { Dayjs } from 'dayjs'

/* Utils */
import {
  CPIFrequency,
  CPITimePeriod,
  CustomLabelCountI,
  CustomLabelDate,
  CustomLabelI,
  CustomLabelsDataPayload,
  CustomLabelsListI,
  DateRangeI,
  VisibleLabel
} from 'models'
import { dateFormat } from 'lib/utils'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'

/* Components */
import { Drawer } from './LabelsDrawer.styles'
import BackSvg from 'ui/components/svg/labels/BackSvg'
import LabelsDrawerContent from './sections/LabelsDrawerContent'

interface Props {
  chartDateRange: DateRangeI
  editedLabel: any
  loading: boolean
  appliedFilter: any
  cpiName: string | undefined
  cpiConfId: string | undefined
  customDate: CustomLabelDate
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  setLoading: Dispatch<SetStateAction<boolean>>
  customLabels: CustomLabelsListI
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  customLabelsCount: CustomLabelCountI
  updateLabels: (payload?: CustomLabelsDataPayload, ignoreCustomPeriod?: boolean) => Promise<void>
  updateLabelsAfterMutation: (payload?: CustomLabelsDataPayload, ignoreCustomPeriod?: boolean) => Promise<void>
  filterLabelsData: (filters: any) => void
  unpinLabel: (labelId: string) => Promise<void>
  pinLabel: (labelId: string) => Promise<void>
  visibleLabels: VisibleLabel[]
  setVisibleLabels: Dispatch<SetStateAction<VisibleLabel[]>>
  deleteLabelCallback?: (labelId: string) => void
}

const LabelsDrawer: FC<Props> = ({
  chartDateRange,
  setLoading,
  setDrawerOpen,
  customDate,
  cpiName,
  cpiConfId,
  customLabels,
  customLabelsCount,
  updateLabels,
  updateLabelsAfterMutation,
  selectedTimePeriod,
  selectedFrequency,
  loading,
  editedLabel,
  unpinLabel,
  pinLabel,
  visibleLabels,
  setVisibleLabels,
  deleteLabelCallback
}) => {
  const { labelDrawerOpen, mode, setMode, redirectToMode, setEditLabel } = useLibraryDetails()

  const [dateRange, setDateRange] = useState({} as DateRangeI)
  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs(dateRange.minDate || ''),
    dayjs(dateRange.maxDate || '')
  ])
  const [pinningLabel, setPinningLabel] = useState(false)

  useEffect(() => {
    if (editedLabel) setEditLabel(editedLabel)
  }, [editedLabel])

  useEffect(() => {
    if (['show-0', 'show-1'].includes(mode)) {
      setDateRange(chartDateRange)
      setSelectedDateRange([
        chartDateRange.minDate ? dayjs(chartDateRange.minDate) : null,
        chartDateRange.maxDate ? dayjs(chartDateRange.maxDate) : null
      ])
    }
  }, [mode, chartDateRange])

  useEffect(() => {
    if (customDate.start && customDate.end) {
      const newDateRange = {
        minDate: customDate.start,
        maxDate: customDate.end
      }
      setDateRange(newDateRange)
      setSelectedDateRange([dayjs(customDate.start), dayjs(customDate.end)])
    } else if (customDate.start && !customDate.end) {
      const newDateRange = {
        minDate: customDate.start,
        maxDate: customDate.start
      }
      setDateRange(newDateRange)
      setSelectedDateRange([dayjs(customDate.start), dayjs(customDate.start)])
    }
  }, [labelDrawerOpen, selectedTimePeriod, selectedFrequency, customDate])

  const handleDateRangeChange = (newRange: [Dayjs | null, Dayjs | null]) => {
    setSelectedDateRange(newRange)

    if (newRange[0] && newRange[1]) {
      const newDateRange = {
        minDate: newRange[0].format('YYYY-MM-DD'),
        maxDate: newRange[1].format('YYYY-MM-DD')
      }
      setDateRange(newDateRange)
    }
  }

  const changeMode = (newMode: string) => {
    setMode(newMode)
  }

  const updateEditLabel = (label: CustomLabelI) => {
    setEditLabel(label)
  }

  const drawerTitle = () => {
    if (mode === 'create') return 'Create Label'
    if (mode === 'edit') return 'Edit Label'
    if (mode === 'filter') return 'Filter Labels'

    return 'Labels'
  }

  const returnToZero = () => {
    setMode('show-0')
    setEditLabel({} as CustomLabelI)
  }

  return (
    <Drawer anchor="right" open={labelDrawerOpen} onClose={() => setDrawerOpen(false)}>
      <Box role="presentation">
        <Box className="wrapper-inner">
          {loading && (
            <Box className="loading-wrapper">
              <Box display="flex" justifyContent="center" style={{ marginTop: '15px' }}>
                <CircularProgress size="36px" color="primary" />
              </Box>
            </Box>
          )}

          <Box className="labels-drawer-header-block">
            <Box className={`header ${mode === 'filter' ? 'custom-header' : ''}`}>
              {mode === 'filter' && (
                <IconButton className="close-icon" onClick={() => setMode(redirectToMode || 'show-0')}>
                  <BackSvg />
                </IconButton>
              )}

              <Box className="header-title-wrapper">
                <Typography className="labels-drawer-header-title">{drawerTitle()}</Typography>
                {!mode?.includes('show') && mode !== 'filter' && (
                  <Typography className="labels-drawer-header-subtitle">
                    {dateRange.maxDate === '' ? (
                      dateRange.minDate
                    ) : dateRange.minDate === dateRange.maxDate ? (
                      dayjs(dateRange.minDate).format(dateFormat.shortUnComma)
                    ) : (
                      <>
                        {dayjs(dateRange.minDate).format(dateFormat.shortUnComma)} -{' '}
                        {dayjs(dateRange.maxDate).format(dateFormat.shortUnComma)}
                      </>
                    )}
                  </Typography>
                )}
              </Box>

              {['edit', 'create'].includes(mode) ? (
                <IconButton className="close-icon" onClick={returnToZero}>
                  <CloseIcon />
                </IconButton>
              ) : (
                mode !== 'filter' && (
                  <IconButton className="close-icon" onClick={() => setDrawerOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                )
              )}
            </Box>
          </Box>

          <Box className="labels-drawer-content">
            <LabelsDrawerContent
              mode={mode}
              setLoading={setLoading}
              dateRange={dateRange}
              loading={loading}
              customLabels={customLabels}
              customLabelsCount={customLabelsCount}
              updateEditLabel={updateEditLabel}
              updateLabels={updateLabels}
              updateLabelsAfterMutation={updateLabelsAfterMutation}
              customDate={customDate}
              unpinLabel={unpinLabel}
              pinLabel={pinLabel}
              visibleLabels={visibleLabels}
              setVisibleLabels={setVisibleLabels}
              pinningLabel={pinningLabel}
              setPinningLabel={setPinningLabel}
              selectedFrequency={selectedFrequency}
              updateMode={changeMode}
              redirectToMode={redirectToMode}
              cpiName={cpiName}
              cpiConfId={cpiConfId}
              returnToZero={returnToZero}
              selectedDateRange={selectedDateRange}
              setSelectedDateRange={handleDateRangeChange}
              deleteLabelCallback={deleteLabelCallback}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

export default LabelsDrawer
